import { Component, OnInit, ElementRef, ViewChild, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router'; 
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AuthService } from '../auth.service';
import { LanguageService } from '../language.service';
import { TranslationService } from '../translation.service';
import { HeaderComponent } from '../header/header.component';
import { TransliterateService } from '../transliterate.service'; 

@Component({
  selector: 'app-new',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule, HeaderComponent], 
  templateUrl: './new.component.html',
  styleUrl: './new.component.css'
})
export class NewComponent implements OnInit {
  categories: any[] = [];
  storyForm: FormGroup;
  storyId: number | null = null;
  tagsList: string[] = [];
  selectedFile: File | null = null;
  imagePreview: string | ArrayBuffer | null = null;
  selectedLanguage: string = 'en';
  showCreateCoverPopup = false;
  message: string = '';
  descriptionWordCount: number = 0;
  descriptionLimitExceeded: boolean = false;
  
  // Auto-suggestion variables
  showTitleSuggestions: boolean = false;
  showDescriptionSuggestions: boolean = false;
  titleSuggestions: string[] = [];
  descriptionSuggestions: string[] = [];
  selectedSuggestionIndex: number = -1;
  private transliterationTimeout: any;

  // Track current word being typed
  private currentWord: string = '';
  private textBeforeCursor: string = '';
  private textAfterCursor: string = '';
  private lastKeyPressed: string = '';

  @ViewChild('titleInput') titleInput!: ElementRef<HTMLInputElement>;
  @ViewChild('descriptionTextarea') descriptionTextarea!: ElementRef<HTMLTextAreaElement>;

  constructor(
    private fb: FormBuilder, 
    private router: Router,
    private route: ActivatedRoute,  
    private http: HttpClient,
    private authService: AuthService,
    public translationService: TranslationService,
    private languageService: LanguageService,
    private transliterationService: TransliterateService,
  ) {
    this.storyForm = this.fb.group({
      story_title: ['', Validators.required],
      story_description: ['', Validators.required],
      tagsInput: [''],
      tags: [''],
      copyright: ['1', Validators.required],
      audiences: ['All Audiences', Validators.required],
      language: ['English', Validators.required],
      category: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.selectedLanguage = this.languageService.getCurrentLanguage();
    
    this.languageService.currentLanguage$.subscribe(lang => {
      this.selectedLanguage = lang;
      this.updateTransliterationSettings();
      this.syncFormLanguage();
    });

    const userId = this.authService.getCurrentUserId();
    if (!userId) {
      this.router.navigate(['login']);
      return;
    }

    this.getCategories();

    this.route.paramMap.subscribe(params => {
      const idParam = params.get('id');
      if (idParam) {
        this.storyId = +idParam;
        this.fetchStoryDetails(this.storyId);
      }
    });
  }
  private syncFormLanguage(): void {
    const currentLangCode = this.languageService.getCurrentLanguage();
    const dbLanguage = this.languageService.getCurrentLanguageForDB();
    
    // Update form with the database language value
    this.storyForm.patchValue({
      language: dbLanguage
    });
    
    console.log('Language synced:', currentLangCode, '->', dbLanguage);
  }

  private updateTransliterationSettings(): void {
    const indianLanguages = ['hi', 'mr', 'bn', 'ta', 'te'];
    
    if (indianLanguages.includes(this.selectedLanguage)) {
      this.transliterationService.setTransliteration(true);
      this.transliterationService.setLanguage(this.selectedLanguage);
    } else {
      this.transliterationService.setTransliteration(false);
      this.hideAllSuggestions();
    }
  }

  // Extract current word being typed - FIXED LOGIC
  private extractCurrentWord(text: string, cursorPosition: number): { 
    currentWord: string, 
    textBeforeCursor: string,
    textAfterCursor: string 
  } {
    const textBeforeCursor = text.substring(0, cursorPosition);
    const textAfterCursor = text.substring(cursorPosition);
    
    // Find the current word by looking for word boundaries
    let wordStart = cursorPosition - 1;
    let wordEnd = cursorPosition;
    
    // Find start of current word
    while (wordStart >= 0 && !/\s/.test(text[wordStart])) {
      wordStart--;
    }
    wordStart++; // Move to first character of word
    
    // Find end of current word
    while (wordEnd < text.length && !/\s/.test(text[wordEnd])) {
      wordEnd++;
    }
    
    const currentWord = text.substring(wordStart, wordEnd);
    
    return { 
      currentWord, 
      textBeforeCursor, 
      textAfterCursor 
    };
  }

  // Title input handler
  onTitleInput(event: any): void {
    const text = event.target.value;
    const cursorPosition = event.target.selectionStart;
    
    const { currentWord, textBeforeCursor, textAfterCursor } = this.extractCurrentWord(text, cursorPosition);
    this.currentWord = currentWord;
    this.textBeforeCursor = textBeforeCursor;
    this.textAfterCursor = textAfterCursor;
    
    console.log('Title Input - Current Word:', currentWord, 'Before:', textBeforeCursor, 'After:', textAfterCursor);
    
    if (this.shouldTransliterate() && currentWord.trim() && currentWord.length > 0) {
      clearTimeout(this.transliterationTimeout);
      this.transliterationTimeout = setTimeout(() => {
        this.getTitleSuggestions(currentWord);
      }, 500);
    } else {
      this.hideTitleSuggestions();
    }
  }

  // Description input handler
  onDescriptionInput(event: any): void {
    this.checkWordLimit(event);
    const text = event.target.value;
    const cursorPosition = event.target.selectionStart;
    
    const { currentWord, textBeforeCursor, textAfterCursor } = this.extractCurrentWord(text, cursorPosition);
    this.currentWord = currentWord;
    this.textBeforeCursor = textBeforeCursor;
    this.textAfterCursor = textAfterCursor;
    
    console.log('Description Input - Current Word:', currentWord, 'Before:', textBeforeCursor, 'After:', textAfterCursor);
    
    if (this.shouldTransliterate() && currentWord.trim() && currentWord.length > 0) {
      clearTimeout(this.transliterationTimeout);
      this.transliterationTimeout = setTimeout(() => {
        this.getDescriptionSuggestions(currentWord);
      }, 600);
    } else {
      this.hideDescriptionSuggestions();
    }
  }

  private shouldTransliterate(): boolean {
    const indianLanguages = ['hi', 'mr', 'bn', 'ta', 'te'];
    return indianLanguages.includes(this.selectedLanguage);
  }

  private getTitleSuggestions(currentWord: string): void {
    // Don't transliterate if word is already in target language
    if (this.isDevanagariWord(currentWord)) {
      this.hideTitleSuggestions();
      return;
    }

    this.transliterationService.transliterateText(currentWord, this.selectedLanguage)
      .subscribe({
        next: (result) => {
          console.log('Title suggestions result:', result);
          if (result.success && result.suggestions && result.suggestions.length > 0) {
            // Show only word suggestions, not full text
            this.titleSuggestions = result.suggestions.slice(0, 3);
            this.showTitleSuggestions = true;
            this.selectedSuggestionIndex = -1;
          } else {
            this.hideTitleSuggestions();
          }
        },
        error: (error) => {
          console.error('Title suggestions error:', error);
          this.hideTitleSuggestions();
        }
      });
  }

  private getDescriptionSuggestions(currentWord: string): void {
    // Don't transliterate if word is already in target language
    if (this.isDevanagariWord(currentWord)) {
      this.hideDescriptionSuggestions();
      return;
    }

    this.transliterationService.transliterateText(currentWord, this.selectedLanguage)
      .subscribe({
        next: (result) => {
          console.log('Description suggestions result:', result);
          if (result.success && result.suggestions && result.suggestions.length > 0) {
            // Show only word suggestions, not full text
            this.descriptionSuggestions = result.suggestions.slice(0, 3);
            this.showDescriptionSuggestions = true;
            this.selectedSuggestionIndex = -1;
          } else {
            this.hideDescriptionSuggestions();
          }
        },
        error: (error) => {
          console.error('Description suggestions error:', error);
          this.hideDescriptionSuggestions();
        }
      });
  }

  // Check if word is already in Devanagari script
  private isDevanagariWord(word: string): boolean {
    // Basic Devanagari Unicode range check
    return /[\u0900-\u097F]/.test(word);
  }

  // Check if word is in English (basic detection)
  private isEnglishWord(word: string): boolean {
    return /^[a-zA-Z]+$/.test(word);
  }

  // Select suggestion - replace current English word with selected suggestion
 selectSuggestion(suggestion: string, field: 'title' | 'description'): void {
  const controlName = field === 'title' ? 'story_title' : 'story_description';
  const currentValue = this.storyForm.get(controlName)?.value || '';

  const words = currentValue.trim().split(/\s+/);
  words.pop(); // remove last typed word

  words.push(suggestion);
  const newValue = words.join(' ') + ' ';

  this.storyForm.patchValue({ [controlName]: newValue });
  this.currentWord = '';

  setTimeout(() => {
    const el = field === 'title' 
      ? this.titleInput.nativeElement 
      : this.descriptionTextarea.nativeElement;

    el.focus();
    el.setSelectionRange(newValue.length, newValue.length);
  }, 10);

  this.hideTitleSuggestions();
  this.hideDescriptionSuggestions();
}

  // Keyboard navigation - FIXED SPACE HANDLING
  onInputKeydown(event: KeyboardEvent, field: 'title' | 'description'): void {
    this.lastKeyPressed = event.key;
    
    const suggestions = field === 'title' ? this.titleSuggestions : this.descriptionSuggestions;
    const showSuggestions = field === 'title' ? this.showTitleSuggestions : this.showDescriptionSuggestions;

   if (event.key === ' ') {

  // If last typed word is English → convert
  if (this.isEnglishWord(this.currentWord)) {
    this.convertLastWord(field);
  }

  this.currentWord = ''; // reset current word
  return; // allow space naturally
}



    if (!showSuggestions || suggestions.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedSuggestionIndex = 
          (this.selectedSuggestionIndex + 1) % suggestions.length;
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.selectedSuggestionIndex = 
          this.selectedSuggestionIndex <= 0 
            ? suggestions.length - 1 
            : this.selectedSuggestionIndex - 1;
        break;

      case 'Enter':
        if (this.selectedSuggestionIndex >= 0) {
          event.preventDefault();
          this.selectSuggestion(suggestions[this.selectedSuggestionIndex], field);
        }
        break;

      case 'Escape':
        event.preventDefault();
        if (field === 'title') {
          this.hideTitleSuggestions();
        } else {
          this.hideDescriptionSuggestions();
        }
        break;
    }
  }


 convertLastWord(field: 'title' | 'description'): void {
  const controlName = field === 'title' ? 'story_title' : 'story_description';
  const currentValue = this.storyForm.get(controlName)?.value || '';

  const words = currentValue.trim().split(/\s+/);
  const lastWord = words.pop();

  if (!lastWord) return;

  // Correct API function
  this.transliterationService.transliterateWord(lastWord, this.selectedLanguage)
    .subscribe((converted: string) => {

      words.push(converted);
      const newValue = words.join(' ') + ' ';

      this.storyForm.patchValue({ [controlName]: newValue });

      setTimeout(() => {
        const el = field === 'title'
          ? this.titleInput.nativeElement
          : this.descriptionTextarea.nativeElement;

        el.focus();
        el.setSelectionRange(newValue.length, newValue.length);
      }, 10);
    });
}

  // Auto-convert on space (separate function that can be called if needed)
  autoConvertOnSpace(field: 'title' | 'description'): void {
  const suggestions = field === 'title' ? this.titleSuggestions : this.descriptionSuggestions;

  if (suggestions.length > 0 && this.isEnglishWord(this.currentWord)) {
    this.selectSuggestion(suggestions[0], field);

    setTimeout(() => {
      const controlName = field === 'title' ? 'story_title' : 'story_description';
      const currentElement = field === 'title' 
        ? this.titleInput.nativeElement 
        : this.descriptionTextarea.nativeElement;

      const currentValue = this.storyForm.get(controlName)?.value || '';
      const newValue = currentValue + ' ';

      this.storyForm.patchValue({
        [controlName]: newValue
      });

      currentElement.focus();
      currentElement.setSelectionRange(newValue.length, newValue.length);

      // Hide suggestions after auto insert
      if (field === 'title') this.hideTitleSuggestions();
      else this.hideDescriptionSuggestions();
    }, 20);
  }
}

  // Hide methods
  hideTitleSuggestions(): void {
    this.showTitleSuggestions = false;
    this.titleSuggestions = [];
    this.selectedSuggestionIndex = -1;
  }

  hideDescriptionSuggestions(): void {
    this.showDescriptionSuggestions = false;
    this.descriptionSuggestions = [];
    this.selectedSuggestionIndex = -1;
  }

  hideAllSuggestions(): void {
    this.hideTitleSuggestions();
    this.hideDescriptionSuggestions();
  }

  // Click outside to hide
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    
    if (!target.closest('.suggestions-container') && 
        !target.closest('#titleInput') && 
        !target.closest('#descriptionTextarea')) {
      this.hideAllSuggestions();
    }
  }

  // Rest of your existing methods remain exactly the same...
  t(key: string): string {
    return this.translationService.translate(key);
  }

  fetchStoryDetails(id: number) {
    const userId = this.authService.getCurrentUserId();
    if (!userId) {
      this.router.navigate(['login']);
      return;
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    this.http.get<any>('https://stories.funtelstudio.com/storyapi/save_story.php?id=' + id, {
      headers: headers,
      withCredentials: true
    }).subscribe({
      next: (res) => {
        if (res.status === 'success') {
          const story = res.story;
          
          if (story.user_id != userId) {
            this.router.navigate(['/404']);
            return;
          }

          this.storyForm.patchValue({
            story_title: story.story_title,
            story_description: story.description,
            tags: story.tags,
            copyright: story.copyright,
            audiences: story.audiences,
            language: story.language,
            category: story.category
          });

          window.scrollTo({ top: 0, behavior: 'smooth' });

          if (story.tags) {
            this.tagsList = story.tags.split(',');
          }

          if (story.story_image) {
            this.imagePreview = 'https://stories.funtelstudio.com/storyapi/' + story.story_image;
          }
        }
      },
      error: (err) => {
        console.error('Error fetching story:', err);
        this.router.navigate(['/404']);
      }
    });
  }

  onTagsKeyUp(event: any) {
    if (event.key === ',' || event.key === 'Enter') {
      event.preventDefault();
      this.addTag();
    }
  }

  checkWordLimit(event: Event) {
    const input = (event.target as HTMLTextAreaElement).value;
    const words = input.trim().split(/\s+/).filter(word => word.length > 0);
    this.descriptionWordCount = words.length;

    if (this.descriptionWordCount > 200) {
      this.descriptionLimitExceeded = true;
      const trimmedText = words.slice(0, 200).join(' ');
      this.storyForm.get('story_description')?.setValue(trimmedText);
      this.descriptionWordCount = 200;
    } else {
      this.descriptionLimitExceeded = false;
    }
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    this.handleFile(file);
  }

  openCreateCoverPopup() {
    this.showCreateCoverPopup = true;
  }

  closeCreateCoverPopup() {
    this.showCreateCoverPopup = false;
  }

  getCoverPrompt(): string {
    const storyTitle = this.storyForm.get('story_title')?.value || 'Your Story Title';
    const category = this.storyForm.get('category')?.value || 'Horror';
    
    return `Vertical 9:16 ultra-realistic ${category.toLowerCase()} poster of "${storyTitle}" in bold cracked white Hindi typography with "${category} Story" in red below. {Describe characters which you want in the poster.}`;
  }

  copyPromptToClipboard() {
    const prompt = this.getCoverPrompt();
    navigator.clipboard.writeText(prompt).then(() => {
      alert('Prompt copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  }

  getCategories() {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    this.http.get<any>('https://stories.funtelstudio.com/storyapi/category.php', { headers, withCredentials: true }).subscribe({
      next: (res) => {
        if (res.status === 'success' && Array.isArray(res.data)) {
          this.categories = res.data;
          console.log('Categories loaded:', this.categories)
        } else {
          this.categories = [];
        }
      },
      error: (err) => {
        console.error('Failed to load categories:', err);
        this.categories = [];
      }
    });
  }

  onFileDropped(event: DragEvent) {
    event.preventDefault();
    const file = event.dataTransfer?.files[0];
    if (file) {
      this.handleFile(file);
    }
  }

  handleFile(file: File) {
    if (file && file.size <= 1024 * 1024) {
      this.selectedFile = file;

      const reader = new FileReader();
      reader.onload = () => {
        this.imagePreview = reader.result;
      };
      reader.readAsDataURL(file);
    } else {
      alert('File size must be 1MB or less.');
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  addTag() {
    const tagsInput = this.storyForm.get('tagsInput')?.value.trim();
    if (tagsInput) {
      const newTags = tagsInput.split(',')
        .map((tag:any) => tag.trim())
        .filter((tag:any) => tag.length > 0);
      
      newTags.forEach((tag:any) => {
        if (!this.tagsList.includes(tag)) {
          this.tagsList.push(tag);
        }
      });
      
      this.storyForm.get('tags')?.setValue(this.tagsList.join(','));
      this.storyForm.get('tagsInput')?.setValue('');
    }
  }

  removeTag(index: number) {
    this.tagsList.splice(index, 1);
    this.storyForm.get('tags')?.setValue(this.tagsList.join(','));
  }

  onSubmit() {
    if (this.storyForm.invalid) {
      this.storyForm.markAllAsTouched();
      this.message = 'Please fill all required fields.';
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    if (this.descriptionLimitExceeded) {
      this.message = 'Description cannot exceed 200 words.';
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    if (this.storyForm.valid) {
      const formData = new FormData();

      formData.append('story_title', this.storyForm.get('story_title')?.value);
      formData.append('story_description', this.storyForm.get('story_description')?.value);
      formData.append('tags', this.storyForm.get('tags')?.value);
      formData.append('copyright', this.storyForm.get('copyright')?.value);
      formData.append('audiences', this.storyForm.get('audiences')?.value);
      formData.append('language', this.storyForm.get('language')?.value);
      formData.append('category', this.storyForm.get('category')?.value);

      if (this.selectedFile) {
        formData.append('cover_image', this.selectedFile);
      }

      const userId = this.authService.getCurrentUserId();
      if (!userId) {
        alert('You must be logged in to save a story.');
        this.router.navigate(['login']);
        return;
      }

      formData.append('user_id', userId.toString());
      if (this.storyId) {
        formData.append('story_id', this.storyId.toString());
      }

      this.logFormData(formData);

      this.http.post<any>(
        'https://stories.funtelstudio.com/storyapi/save_story.php',
        formData,
        {
          withCredentials: true,
          observe: 'response'
        }
      ).subscribe({
        next: (response) => {
          const res = response.body;
          if (res?.status === 'success') {
            this.message = this.storyId ? 'Story updated successfully' : 'Story created successfully';
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setTimeout(() => { this.message = ''; }, 3000);

            if (!this.storyId && res.story_id) {
              this.router.navigate(['/creat', res.story_id]);
            }
          } else {
            this.message = res?.message || 'Operation completed but with unexpected status';
          }
        },
        error: (err) => {
          console.error('Full error:', err);
          this.message = err?.error?.message || err?.error?.error || err?.message || 'Network/server error occurred';
        }
      });
    }
  }

  logFormData(formData: FormData) {
    console.log('FormData contents:');
    const formDataEntries = (formData as any).entries();
    for (const pair of formDataEntries) {
      console.log(pair[0] + ': ', pair[1]);
    }
  }

  validateFormBeforeSubmit() {
    console.log('Form validity:', this.storyForm.valid);
    console.log('Form values:', this.storyForm.value);
    console.log('User ID:', this.authService.getCurrentUserId());
    console.log('Story ID:', this.storyId);
    console.log('Selected file:', this.selectedFile ? this.selectedFile.name : 'None');
  }

  writenew() {
    this.onSubmit();
  }
}